import type { Metric, MetricUnit } from './types'

const numberFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 })
const currencyFmt = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

export function formatMetric(value: number, unit: MetricUnit): string {
  switch (unit) {
    case 'percent':
      return `${numberFmt.format(value)}%`
    case 'points':
      return `${numberFmt.format(value)} pts`
    case 'hours':
      return `${numberFmt.format(value)} h`
    case 'days':
      return `${numberFmt.format(value)} d`
    case 'currency':
      return currencyFmt.format(value)
    case 'ratio':
      return `${numberFmt.format(value)}×`
    default:
      return numberFmt.format(value)
  }
}

/**
 * Whether a change is an improvement, honoring the metric's direction. A drop
 * in "Defects / Sprint" (direction 'down') is good; a drop in velocity is not.
 * Returns null when there's nothing to compare against.
 */
export function trendOf(
  metric: Pick<Metric, 'direction'>,
  current: number,
  previous: number | undefined,
): { delta: number; pct: number | null; better: boolean } | null {
  if (previous === undefined) return null
  const delta = current - previous
  if (delta === 0) return { delta: 0, pct: 0, better: true }
  const better = metric.direction === 'up' ? delta > 0 : delta < 0
  const pct = previous === 0 ? null : (delta / Math.abs(previous)) * 100
  return { delta, pct, better }
}

/**
 * Parse a value from the database into a Date for display.
 *
 * A date-only string is parsed as LOCAL midnight, not UTC. `new Date('2026-05-20')`
 * is midnight UTC, which in any negative offset is the 19th — so a `conducted_on`
 * or `period_start` rendered straight through showed the day before all afternoon
 * in US timezones. Timestamps keep the platform parse, since those really are
 * instants.
 */
export function localDate(value: string | Date): Date {
  if (value instanceof Date) return value
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
  }
  return new Date(value)
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return localDate(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "3 minutes ago" / "in 2 days" — for message timestamps and schedules. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const diffSec = Math.round((then - Date.now()) / 1000)
  const abs = Math.abs(diffSec)

  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (abs < 45) return rtf.format(Math.round(diffSec), 'second')
  if (abs < 45 * 60) return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 22 * 3600) return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 26 * 86400) return rtf.format(Math.round(diffSec / 86400), 'day')
  if (abs < 320 * 86400) return rtf.format(Math.round(diffSec / (86400 * 30)), 'month')
  return rtf.format(Math.round(diffSec / (86400 * 365)), 'year')
}

/** Initials for an avatar fallback: "Chad Michel" → "CM". */
export function initials(name: string | null | undefined, email?: string): string {
  const source = name?.trim() || email?.split('@')[0] || '?'
  const parts = source.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function displayName(p: { display_name: string | null; email: string } | null | undefined): string {
  if (!p) return 'Unknown'
  return p.display_name?.trim() || p.email.split('@')[0]
}

/**
 * Suggest the next period label for a metric, so recording a reading is one
 * click rather than remembering what you called the last one. Increments a
 * trailing number when there is one ("Sprint 11" → "Sprint 12").
 */
export function nextPeriodLabel(metric: Metric, lastLabel: string | undefined): string {
  if (lastLabel) {
    const m = lastLabel.match(/^(.*?)(\d+)\s*$/)
    if (m) return `${m[1]}${Number(m[2]) + 1}`
  }
  const now = new Date()
  switch (metric.period_kind) {
    case 'sprint':
      return 'Sprint 1'
    case 'week': {
      // ISO week number.
      const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
      const dayNum = d.getUTCDay() || 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum)
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
      const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
      return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`
    }
    case 'month':
      return now.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    case 'quarter':
      return `Q${Math.floor(now.getMonth() / 3) + 1} ${now.getFullYear()}`
    default:
      return now.toISOString().slice(0, 10)
  }
}
